import { ProjectMetadata, Chapter } from '../types';
import { resolveCanonicalChapterNumber } from './chapterResolver';

export type NormalizedChapterForCounting = {
  chapterNumber: number;
  content: string;
  contentSource: "inline" | "storage" | "none";
  storageContentVerified: boolean;
  originalId?: string | number;
};

export type CompletionState = {
  targetChapterCount: number;
  completedChapterCount: number;
  uniqueChapterNumbers: number[];
  missingChapterNumbers: number[];
  duplicateChapterNumbers: number[];
  isBookComplete: boolean;
  hasOverflowChapters: boolean;
  isAuthoritative: boolean;
  invalidChapterRecords: number;
  orphanedStorageFiles?: number;
  orphanedChapterRecords?: number;
};

export type ManuscriptWorkspaceState = CompletionState & {
  existingChapterNumbers: number[];
  highestExistingChapterNumber: number;
  firstMissingChapterNumber: number | null;
  manuscriptIsComplete: boolean;
  canGenerateNextSequentialChapter: boolean;
  hasInternalGap: boolean;
  nextSequentialChapterNumber: number | null;
};

function chapterExistsInIndex(chapter: Chapter | Record<string, unknown>): boolean {
  const chapterNumber = resolveCanonicalChapterNumber(chapter);
  if (isNaN(chapterNumber) || chapterNumber <= 0) return false;
  const hasInline = typeof chapter.content === 'string' && chapter.content.trim().length > 0;
  const hasStorage =
    typeof chapter.storagePath === 'string' && chapter.storagePath.trim().length > 0;
  return hasInline || hasStorage;
}

export function getIndexedChapterNumbers(chapters: Chapter[]): number[] {
  const nums = chapters
    .filter((ch) => chapterExistsInIndex(ch))
    .map((ch) => resolveCanonicalChapterNumber(ch))
    .filter((n) => !isNaN(n) && n > 0);
  return Array.from(new Set(nums)).sort((a, b) => a - b);
}

export const normalizeChapter = (chapter: Chapter | any, isAuthoritativeContext: boolean): NormalizedChapterForCounting | null => {
  if (!chapter) return null;

  const chapterNumber = resolveCanonicalChapterNumber(chapter);
  if (isNaN(chapterNumber) || chapterNumber <= 0) return null;

  const hasInlineContent = typeof chapter.content === "string" && chapter.content.trim().length > 0;
  const hasStoragePath = typeof chapter.storagePath === "string" && chapter.storagePath.trim().length > 0;

  let contentSource: "inline" | "storage" | "none" = hasInlineContent ? "inline" : (hasStoragePath ? "storage" : "none");
  let content = hasInlineContent ? chapter.content : "";
  
  // Storage verification must come from a trusted hydration process, not just old metadata
  // We completely ignore chapter.isStorageVerified from persisted payloads.
  let storageContentVerified = false;
  if (!hasInlineContent && hasStoragePath) {
     if (chapter.contentSource === "storage" && chapter.storageContentVerified === true) {
         storageContentVerified = true;
         contentSource = "storage";
         content = typeof chapter.content === 'string' ? chapter.content : "";
     } else if (typeof chapter.content === "string" && chapter.content.trim().length > 0) {
         // If it was fetched and populated inline temporarily but missing explicit flags
         storageContentVerified = true;
         contentSource = "storage";
         content = chapter.content;
     }
  }

  return {
    chapterNumber,
    content,
    contentSource,
    storageContentVerified,
    originalId: chapter.id
  };
};

export const getCanonicalCompletionState = (
  chapters: Chapter[],
  targetChapterCount: number,
  isAuthoritativeContext: boolean
): CompletionState => {
  const normalized = chapters
    .map(ch => normalizeChapter(ch, isAuthoritativeContext))
    .filter((ch): ch is NormalizedChapterForCounting => ch !== null);

  const validChapters = normalized.filter(ch => {
    return (
      (typeof ch.content === "string" && ch.content.trim().length > 0 && ch.contentSource === "inline") ||
      (ch.contentSource === "storage") // A Storage-backed chapter counts as saved even before its prose loads into memory
    );
  });

  const uniqueChapterNumbers = Array.from(new Set(validChapters.map(ch => ch.chapterNumber))).sort((a, b) => a - b);
  
  const allChapterNumbers = validChapters.map(ch => ch.chapterNumber);
  const duplicateChapterNumbers = Array.from(new Set(allChapterNumbers.filter((item, index) => allChapterNumbers.indexOf(item) !== index)));

  const invalidChapterRecords = chapters.length - validChapters.length;

  const missingChapterNumbers: number[] = [];
  const maxNum = Math.max(...uniqueChapterNumbers, targetChapterCount > 0 ? targetChapterCount : 0);
  for (let i = 1; i <= maxNum; i++) {
    if (!uniqueChapterNumbers.includes(i) && (targetChapterCount === 0 || i <= targetChapterCount)) {
      missingChapterNumbers.push(i);
    }
  }

  const completedChapterCount = uniqueChapterNumbers.length;

  const manuscriptIsComplete =
    targetChapterCount > 0 &&
    duplicateChapterNumbers.length === 0 &&
    missingChapterNumbers.filter((n) => n >= 1 && n <= targetChapterCount).length === 0 &&
    uniqueChapterNumbers.length === targetChapterCount;

  const isBookComplete = manuscriptIsComplete;
  const hasOverflowChapters =
    targetChapterCount > 0 &&
    uniqueChapterNumbers.some((n) => n > targetChapterCount);

  return {
    targetChapterCount,
    completedChapterCount,
    uniqueChapterNumbers,
    missingChapterNumbers,
    duplicateChapterNumbers,
    isBookComplete,
    hasOverflowChapters,
    isAuthoritative: isAuthoritativeContext,
    invalidChapterRecords,
  };
};

/**
 * Canonical manuscript workspace state for next-chapter UI and generation guards.
 * Uses chapter index (inline prose or storage path) so internal gaps are detectable
 * before storage prose is fully hydrated.
 */
export function getManuscriptWorkspaceState(
  chapters: Chapter[],
  targetChapterCount: number,
  isAuthoritativeContext: boolean = false,
): ManuscriptWorkspaceState {
  const completion = getCanonicalCompletionState(chapters, targetChapterCount, isAuthoritativeContext);
  const indexedNumbers = getIndexedChapterNumbers(chapters);
  const existingChapterNumbers =
    indexedNumbers.length > 0 ? indexedNumbers : completion.uniqueChapterNumbers;
  const highestExistingChapterNumber = existingChapterNumbers.length
    ? Math.max(...existingChapterNumbers)
    : 0;

  const missingInTarget =
    targetChapterCount > 0
      ? completion.missingChapterNumbers.filter((n) => n >= 1 && n <= targetChapterCount)
      : completion.missingChapterNumbers;

  const indexedMissing: number[] = [];
  if (targetChapterCount > 0) {
    for (let i = 1; i <= targetChapterCount; i++) {
      if (!existingChapterNumbers.includes(i)) {
        indexedMissing.push(i);
      }
    }
  }

  const missingChapterNumbers =
    indexedNumbers.length > 0 ? indexedMissing : missingInTarget;
  const firstMissingChapterNumber =
    missingChapterNumbers.length > 0 ? missingChapterNumbers[0] : null;

  const hasInternalGap =
    firstMissingChapterNumber != null &&
    highestExistingChapterNumber > 0 &&
    firstMissingChapterNumber < highestExistingChapterNumber;

  const manuscriptIsComplete =
    targetChapterCount > 0 &&
    completion.duplicateChapterNumbers.length === 0 &&
    missingChapterNumbers.length === 0 &&
    existingChapterNumbers.length === targetChapterCount;

  const canGenerateNextSequentialChapter =
    !manuscriptIsComplete &&
    !hasInternalGap &&
    completion.duplicateChapterNumbers.length === 0 &&
    highestExistingChapterNumber < targetChapterCount &&
    firstMissingChapterNumber === highestExistingChapterNumber + 1;

  const nextSequentialChapterNumber = canGenerateNextSequentialChapter
    ? highestExistingChapterNumber + 1
    : null;

  return {
    ...completion,
    missingChapterNumbers,
    existingChapterNumbers,
    highestExistingChapterNumber,
    firstMissingChapterNumber,
    manuscriptIsComplete,
    canGenerateNextSequentialChapter,
    hasInternalGap,
    nextSequentialChapterNumber,
    isBookComplete: manuscriptIsComplete,
  };
}

export function formatInternalGapGenerationMessage(firstMissingChapterNumber: number): string {
  return `Chapter ${firstMissingChapterNumber} is missing, while later chapters already exist. Use Missing Chapter Recovery instead of generating a new chapter.`;
}

export const getVerifiedChapterCount = (project: ProjectMetadata, loadedChapters?: Chapter[], isAuthoritativeContext: boolean = false): number => {
  if (loadedChapters && Array.isArray(loadedChapters)) {
    return getCanonicalCompletionState(loadedChapters, project.targetChapterCount || project.chapterCount || 0, isAuthoritativeContext).completedChapterCount;
  }
  return project.chapterCount || 0; // Fallback only
};

export const getDisplayChapterCount = (project: ProjectMetadata, loadedChapters?: Chapter[], isAuthoritativeContext: boolean = false): number => {
  return getVerifiedChapterCount(project, loadedChapters, isAuthoritativeContext);
};
